import { useState } from 'react'
import { CalendarDays, PhoneCall, UserRound, X } from 'lucide-react'
import { Button, FormError } from '../../../core/index.js'
import { useBoardActions } from '../../hooks.js'
import type { BridgeCallout, NotToldReason, Posting, PostingAudience } from '../../types.js'
import { OpenCalloutModal } from '../OpenCalloutModal.js'
import { ReasonForm } from './ReasonForm.js'
import { WorkerMonthModal } from './WorkerMonthModal.js'

/** Free and able: the supervisor's override places them as it is. */
const ASSIGNABLE: readonly NotToldReason[] = ['free_since_blast', 'not_yet_offered']
/** A month rule stands in the way: the override passes it with a reason code, recorded (plan section 4.7). */
const ASSIGNABLE_WITH_REASON: readonly NotToldReason[] = ['max_consecutive_days', 'day_after_evening']

const REASON_NOTE: Partial<Record<NotToldReason, string>> = {
  max_consecutive_days: 'too many days in a row',
  day_after_evening: 'a day after an evening',
}

interface PostingActionsProps {
  facilityId: string
  period: string
  audience: PostingAudience
  /** Whether the supervisor's override is offered: the facility's order, inside the lock (open shift lifecycle plan D5); first come, never. */
  canAssign: boolean
  /** Only a facility administrator opens a Clinical Bridge callout (the user's rule, 13 October 2026). */
  canOpenCallout: boolean
  /** Who left a vacated seat and why, carried into the callout form; null for a seat the build left open. */
  vacatedFrom?: Posting['vacatedFrom']
  /** The Clinical Bridge callout already out for this seat, if any: a second is not offered while it is open. */
  callout?: BridgeCallout | null
}

/**
 * What staff can do about one open seat beyond waiting on the engine: assign someone it did not tell (free, or past a
 * month rule with a reason code), open a Clinical Bridge callout for it, or, inside the lock, run it short. The hard
 * rules (working that day, left, another unit, a credential) are never offered, since the override refuses them. The
 * calendar at the end of each pill opens that worker's month, read only.
 */
export function PostingActions({ facilityId, period, audience, canAssign, canOpenCallout: mayOpenCallout, vacatedFrom = null, callout = null }: PostingActionsProps) {
  const board = useBoardActions(facilityId, period)
  const [asking, setAsking] = useState<'run_short' | { workerId: string; name: string } | null>(null)
  const [calloutOpen, setCalloutOpen] = useState(false)
  // The worker whose month is open, read only, from the calendar at the end of their pill.
  const [viewing, setViewing] = useState<string | null>(null)
  if (audience.status !== 'open' || audience.phase === 'ended') {
    return null
  }
  const { postingId, shift } = audience
  const free = canAssign ? audience.notTold.filter((group) => ASSIGNABLE.includes(group.reason)).flatMap((group) => group.workers.map((worker) => ({ ...worker, reason: group.reason }))) : []
  const pastRule = canAssign ? audience.notTold.filter((group) => ASSIGNABLE_WITH_REASON.includes(group.reason)).flatMap((group) => group.workers.map((worker) => ({ ...worker, reason: group.reason }))) : []
  const assigningId = board.assign.isPending ? (board.assign.variables?.workerId ?? null) : null
  const pending = board.assign.isPending || board.runShort.isPending
  const canRunShort = audience.phase === 'locked'
  const calloutOut = callout !== null && callout.status === 'open'
  const canOpenCallout = mayOpenCallout && !calloutOut

  return (
    <div className="flex flex-col gap-2 border-t border-marine/10 px-3 py-2">
      {(free.length > 0 || pastRule.length > 0) && (
        <div className="flex flex-col gap-1">
          <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">Assign someone not told</div>
          <div className="flex flex-wrap gap-1.5">
            {[...free.map((worker) => ({ ...worker, pastRule: false })), ...pastRule.map((worker) => ({ ...worker, pastRule: true }))].map((worker) => (
              <span key={worker.workerId} className={`inline-flex items-stretch overflow-hidden rounded-full border text-[11px] font-bold ${worker.pastRule ? 'border-gold/60 bg-gold-lt/40 text-ink' : 'border-marine/25 bg-white text-marine'}`}>
                <button
                  type="button"
                  disabled={pending}
                  aria-busy={assigningId === worker.workerId}
                  title={worker.pastRule ? `Would pass a month rule (${REASON_NOTE[worker.reason]}): a reason code is asked` : undefined}
                  className="inline-flex items-center gap-1 px-2.5 py-1 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => (worker.pastRule ? setAsking({ workerId: worker.workerId, name: worker.name }) : board.assign.mutate({ postingId, workerId: worker.workerId, reasonCode: 'ASSIGNED_FROM_TODAY' }))}
                >
                  <UserRound className="h-3 w-3" aria-hidden="true" />
                  {assigningId === worker.workerId ? `Assigning ${worker.name}…` : worker.pastRule ? `Assign ${worker.name} anyway · ${REASON_NOTE[worker.reason]}` : `Assign ${worker.name}`}
                </button>
                <button
                  type="button"
                  aria-label={`See ${worker.name}'s month`}
                  title={`See ${worker.name}'s month`}
                  className={`inline-flex items-center border-l px-2 ${worker.pastRule ? 'border-gold/40 hover:bg-gold-lt' : 'border-marine/20 hover:bg-marine-lt'}`}
                  onClick={() => setViewing(worker.workerId)}
                >
                  <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {asking === 'run_short' && (
        <ReasonForm
          label="Run short"
          pending={board.runShort.isPending}
          onCancel={() => setAsking(null)}
          onSubmit={(reasonCode) => board.runShort.mutate({ postingId, reasonCode }, { onSuccess: () => setAsking(null) })}
        />
      )}
      {asking !== null && asking !== 'run_short' && (
        <div className="flex flex-col gap-1">
          <p className="text-[11px] text-ink-mid">Assigning {asking.name} passes a month rule; the reason is kept on the record.</p>
          <ReasonForm
            label={`Assign ${asking.name}`}
            pending={board.assign.isPending}
            onCancel={() => setAsking(null)}
            onSubmit={(reasonCode) => board.assign.mutate({ postingId, workerId: asking.workerId, reasonCode }, { onSuccess: () => setAsking(null) })}
          />
        </div>
      )}

      {calloutOut && <p className="text-[11px] leading-snug text-ink-mid">A Clinical Bridge callout is out for this seat. Whoever takes it gets the seat, and the board withdraws its own offers for it.</p>}
      {asking === null && (canOpenCallout || canRunShort) && (
        <div className="flex flex-wrap items-center gap-2">
          {canOpenCallout && (
            <Button icon={PhoneCall} size="sm" onClick={() => setCalloutOpen(true)}>
              Open a Bridge callout
            </Button>
          )}
          {canRunShort && (
            <Button icon={X} size="sm" variant="secondary" onClick={() => setAsking('run_short')}>
              Run short
            </Button>
          )}
        </div>
      )}
      <FormError error={board.assign.error ?? board.runShort.error} fallback="That did not go through. Try again." />
      {viewing !== null && <WorkerMonthModal facilityId={facilityId} period={period} workerId={viewing} seat={shift} onClose={() => setViewing(null)} />}
      {calloutOpen && (
        <OpenCalloutModal
          facilityId={facilityId}
          postingId={postingId}
          onClose={() => setCalloutOpen(false)}
          onOpened={() => setCalloutOpen(false)}
          initial={{
            date: shift.date,
            unitId: shift.unitId,
            start: shift.startTime,
            end: shift.endTime,
            ...(shift.role ? { role: shift.role } : {}),
            ...(vacatedFrom ? { absentWorkerId: vacatedFrom.workerId, reason: vacatedFrom.reason } : {}),
          }}
        />
      )}
    </div>
  )
}
